import _ from 'lodash';
import { sqLicenseManagementStore, sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { Feature } from '@/licenseManagement/licenseManagement.constants';
import { hasValidFeatureInLicense } from '@/licenseManagement/licenseManagement.utilities';
import { isInWorkbookRoute } from '@/main/routing.utilities';
import {
  auditingAllUsersCanRead,
  isEarlyAccessCapabilityEnabled,
  isLockingAndVersioningEnabled,
  isScalingTableCapabilityVisible,
} from '@/services/systemConfiguration.utilities';
import { isPresentationWorkbookMode, isViewOnlyWorkbookMode } from '@/utilities/workbookMode.utilities';
import { sqUserGroupsApi } from '@seeqdev/api/endpoint/UserGroupsApi';
import { CapabilityEnum } from '@seeqdev/api/model/CapabilityInheritanceOutputV1';
import { SeeqNames } from '@seeqdev/constants';
import type { AceOutputV1 } from '@seeqdev/api/model/AceOutputV1';
import type { CapabilityInheritanceOutputV1 } from '@seeqdev/api/model/CapabilityInheritanceOutputV1';
import type { IdentityPreviewV1 } from '@seeqdev/api/model/IdentityPreviewV1';
import type { PermissionsV1 } from '@seeqdev/api/model/models';

export interface Capability extends IdentityPreviewV1 {
  capability: CapabilityEnum;
}

let capabilities: Capability[] | undefined;

/**
 * Reset cached capabilities (test-only).
 */
export const resetCapabilities = () => {
  capabilities = undefined;
};

/**
 * Returns a list of all Capability user groups
 */
export const getAllCapabilities = async (): Promise<Capability[]> => {
  if (capabilities) return capabilities;

  return await sqUserGroupsApi.getCapabilityUserGroups({}).then(({ data }) => {
    capabilities = _.chain(data.items)
      .reject(
        (cap) =>
          (cap.name === SeeqNames.Capabilities.ScalingTables.DataId && !isScalingTableCapabilityVisible()) ||
          (cap.name === SeeqNames.Capabilities.EarlyAccess.DataId && !isEarlyAccessCapabilityEnabled()),
      )
      .map((cap) => {
        let capabilityEnum: CapabilityEnum;
        if (cap.name === SeeqNames.Capabilities.Administration.DataId) {
          capabilityEnum = CapabilityEnum.Admins; // For Administrators, the name doesn't match the CapabilityEnum :|
        } else {
          capabilityEnum = CapabilityEnum[cap.name as keyof typeof CapabilityEnum];
        }
        return { ...cap, capability: capabilityEnum };
      })
      .value();
    return capabilities;
  });
};

export const getAdministratorGroupId = async (): Promise<string> => {
  const capabilities = await getAllCapabilities();
  const admin = capabilities.find((cap) => cap.capability === CapabilityEnum.Admins);
  return admin?.id ?? (await Promise.reject('Administrators not found'));
};

export const isCapabilityGroup = async (groupID: string) => {
  const capabilities = await getAllCapabilities();
  return capabilities?.some((capability) => capability.id === groupID);
};

const userHasCapability = (capabilityToTest: CapabilityEnum): boolean => {
  return (
    _.findIndex(
      sqWorkbenchStore.currentUser.capabilityGrants,
      (capability: CapabilityInheritanceOutputV1) => capability.capability === capabilityToTest,
    ) >= 0
  );
};

/**
 * Determines if the current user is trusted.
 *
 * @return true if user is trusted, false otherwise
 */
export function isTrustedUser(): boolean {
  return sqWorkbenchStore.currentUser.isTrusted;
}

/**
 * Determines if the Audit Trail logs can be viewed by the current user
 *
 * @return true if the current user can view Audit Trail Logs,
 * otherwise false even if the setting is null or undefined
 */
export function canReadAuditTrail(): boolean {
  return (
    // TODO CRAB-41986: Remove the config check once the config is removed
    isTrustedUser() && (auditingAllUsersCanRead() === true || userHasCapability(CapabilityEnum.AuditTrailCapability))
  );
}

/**
 * Determines if the Version Log page can be viewed by the current user
 *
 * @return true if the current user can view Version Logs,
 * otherwise false even if the setting is null or undefined
 */
export function canReadVersionLogs(): boolean {
  return (
    isTrustedUser() &&
    isLockingAndVersioningEnabled() &&
    hasValidFeatureInLicense(sqLicenseManagementStore.getState().license, Feature.Locking)
  );
}

/**
 * Determines if the current user can read the item
 *
 * @param item - an item
 * @returns true if the user can read the item, false otherwise
 */
export function canReadItem(item: { effectivePermissions?: PermissionsV1 }): boolean {
  return isAdmin() || !!item?.effectivePermissions?.read;
}

/**
 * Determines if the current user can write the item
 *
 * @param item - an item
 * @returns true if the user can write the item, false otherwise
 */
export function canWriteItem(
  item:
    | {
        id?: string;
        name?: string;
        effectivePermissions?: PermissionsV1;
        isLocked?: boolean;
      }
    | null
    | undefined,
): boolean {
  return (isAdmin() || !!item?.effectivePermissions?.write) && !item?.isLocked;
}

/**
 * Determines if the current user can annotate the item
 *
 * @param item - an item
 * @returns true if the user can annotate the item, false otherwise
 */
export function canAnnotateItem(item?: { effectivePermissions?: PermissionsV1 } | null): boolean {
  // Users in Seeq can annotate an item if they have permission to read it
  return isAdmin() || !!item?.effectivePermissions?.read;
}

/**
 * Determines if the current user can manage the item (i.e. view and change ACL permissions)
 *
 * @param item - an item
 * @returns true if the user can manage the item, false otherwise
 */
export function canManageItem(item?: { effectivePermissions?: PermissionsV1 } | null): boolean {
  return isAdmin() || !!item?.effectivePermissions?.manage;
}

/**
 * Determines if a user has exclusive access
 *
 * @param acl - an access control list
 * @param userId - a user ID
 * @returns true if exclusive access; false otherwise
 */
export function userHasExclusiveAccess(acl: AceOutputV1[], userId = sqWorkbenchStore.currentUser.id): boolean {
  return !_.chain(acl)
    .reject((ace) => ace?.identity?.id === userId || !_.some(ace.permissions))
    .some()
    .value() as boolean;
}

/**
 * Determines if the specified workbook can be modified by the current user.
 *
 * @param  workbook - The workbook to test
 * @param  disallowArchived - if true prevent modification of archived workbooks
 * @return True if it can be modified, otherwise false
 */
export function canModifyWorkbook(
  workbook?: {
    id?: string;
    name?: string;
    workbookId?: string;
    effectivePermissions?: PermissionsV1;
    isLocked?: boolean;
    isArchived?: boolean;
  } | null,
  disallowArchived = true,
): boolean {
  if (disallowArchived && workbook?.isArchived) {
    return false;
  }

  return canWriteItem(workbook);
}

/**
 * Determines if the current workbook's document (Topic report or Journal) can be modified based on the current
 * user, route, and view mode.
 *
 * @returns {boolean} true if it can be modified, false otherwise
 */
export const canModifyDocument = () => {
  // Inlines utilities.ts' isInWorkbookRouteAndWorkbookLoaded() rather than importing it. utilities.ts is heavy
  // (badwords-list, object-hash, i18next, administration.constants) and this module is imported by ~113 others.
  // TODO CRAB-63976: pull isInWorkbookRouteAndWorkbookLoaded out of utilities.ts so this can import it directly
  // instead of duplicating it. A quick extraction attempt broke many test suites
  return (
    sqWorkbookStore.isWorkbookLoaded &&
    isInWorkbookRoute() &&
    canModifyWorkbook(sqWorkbookStore) &&
    !isPresentationWorkbookMode() &&
    !isViewOnlyWorkbookMode()
  );
};

export const isWorkbookLocked = (workbook?: { locked?: boolean } | null): boolean => !!workbook?.locked;

/**
 * Allows the user to do something if they are an administrator.
 *
 * @return True if user is an administrator, false otherwise
 */
export function isAdmin(): boolean {
  return !!sqWorkbenchStore.currentUser.isAdmin;
}

/**
 * @return True if current user has the UserAdministrationCapability
 */
export function hasUserAdminCapability(): boolean {
  return userHasCapability(CapabilityEnum.UserAdministrationCapability);
}

/**
 * @return True if current user has the DatasourceAdministrationCapability
 */
export function hasDatasourceAdminCapability(): boolean {
  return userHasCapability(CapabilityEnum.DatasourceAdministrationCapability);
}

/**
 * @return True if current user has the AnalyticsAdministrationCapability
 */
export function hasAnalyticsAdminCapability(): boolean {
  return userHasCapability(CapabilityEnum.AnalyticsAdministrationCapability);
}

/**
 * @return True if current user has the ScalingTableCapability
 */
export function hasScalingTableCapability(): boolean {
  return userHasCapability(CapabilityEnum.ScalingTableCapability);
}

/**
 * @return True if the current user has the EarlyAccessCapability
 */
export function hasEarlyAccessCapability(): boolean {
  return userHasCapability(CapabilityEnum.EarlyAccessCapability);
}

export function showAdministrationMenuItem(): boolean {
  return isAdmin() || hasUserAdminCapability() || hasDatasourceAdminCapability() || hasAnalyticsAdminCapability();
}

/**
 * Allows the user to do something if the value at the supplied property path matches the current user ID.
 *
 * @param userIdPropertyPath - property path to a user ID
 * @return True if the value at the supplied property path matches the current user ID, false otherwise
 */
export function hasCurrentUserId(
  item: { effectivePermissions?: PermissionsV1 } | null | undefined,
  userIdPropertyPath: string,
): boolean {
  return _.get(item, userIdPropertyPath) === sqWorkbenchStore.currentUser.id;
}
